import { collection, query, where, getDocs, doc, updateDoc, addDoc, serverTimestamp } from 'firebase/firestore';
import { db } from '../lib/firebase';

/**
 * Update goal progress when an experience is completed
 */
export async function updateGoalProgress(familyId, experience) {
  if (!familyId || !experience) return;

  try {
    // Get all active goals for this family
    const goalsQuery = query(
      collection(db, 'families', familyId, 'goals'),
      where('completed', '==', false)
    );
    const goalsSnapshot = await getDocs(goalsQuery);
    const goals = goalsSnapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data()
    }));

    // Check each goal
    for (const goal of goals) {
      let shouldUpdate = false;
      let newCount = goal.currentCount || 0;

      // Check if this experience matches the goal criteria
      if (goal.experienceType && experience.type !== goal.experienceType) {
        continue; // Skip if experience type doesn't match
      }

      // Update based on goal type
      if (goal.type === 'count') {
        // Increment count for matching experiences
        shouldUpdate = true;
        newCount = newCount + 1;
      } else if (goal.type === 'streak') {
        // For streaks, we'd need to check consecutive dates
        // For now, just increment (full streak logic would require date checking)
        shouldUpdate = true;
        newCount = newCount + 1;
      } else if (goal.type === 'first-time') {
        // Check if this is the first time completing this experience type
        // This would require checking past experiences
        shouldUpdate = true;
        newCount = 1; // First time = 1
      }

      if (shouldUpdate) {
        const goalRef = doc(db, 'families', familyId, 'goals', goal.id);
        
        // Check if goal is now complete
        const isComplete = newCount >= goal.targetCount;
        
        await updateDoc(goalRef, {
          currentCount: newCount,
          completed: isComplete,
          completedDate: isComplete ? serverTimestamp() : null
        });

        // If goal is complete, create achievement
        if (isComplete) {
          await addDoc(collection(db, 'families', familyId, 'achievements'), {
            goalId: goal.id,
            title: goal.title,
            type: goal.type,
            completedDate: serverTimestamp(),
            celebrationShown: false
          });
        }
      }
    }
  } catch (error) {
    console.error('Error updating goal progress:', error);
  }
}

/**
 * Check for first-time experience badges
 */
export async function checkFirstTimeBadges(familyId, experience) {
  if (!familyId || !experience || !experience.type) return;

  try {
    // Check if this experience type has been completed before
    const { collection: collectionFn, query: queryFn, where: whereFn, getDocs: getDocsFn } = await import('firebase/firestore');
    const experiencesQuery = queryFn(
      collectionFn(db, 'projects'),
      whereFn('familyId', '==', familyId),
      whereFn('type', '==', experience.type),
      whereFn('completed', '==', true)
    );
    const snapshot = await getDocsFn(experiencesQuery);
    
    // If this is the first completed experience of this type
    if (snapshot.size === 1) {
      const { addDoc: addDocFn, serverTimestamp } = await import('firebase/firestore');
      const experienceTypeName = experience.typeName || experience.type;
      
      await addDocFn(collectionFn(db, 'families', familyId, 'achievements'), {
        goalId: null, // Not from a goal
        title: `First ${experienceTypeName}`,
        type: 'first-time',
        experienceType: experience.type,
        completedDate: serverTimestamp(),
        celebrationShown: false
      });
    }
  } catch (error) {
    console.error('Error checking first-time badges:', error);
  }
}
